import { call, put, takeLatest } from "redux-saga/effects";
import type { SagaIterator } from "redux-saga";
import { projectsAPI } from "../api/api";
import { projectsActions, actionTypes } from "../store/reducers/projectsReducer";
import { generalActions } from "../store/reducers/generalReducer";
import { Project } from "../../types/global/types";
import i18n from "../../res/languages";

// Translate toast copy at dispatch time via the live i18n instance so it
// follows the viewer's current language. Backend-provided error messages
// (error.response.data.message) are surfaced as-is when present.
const t = (key: string, opts?: Record<string, unknown>) => i18n.t(`projects.${key}`, opts);

function* fetchProjects(_action: any): SagaIterator {
  try {
    const response: { status: number; data: any } = yield call(projectsAPI.getProjects, 'all');
    if (response.status === 200) {
      const projects: Project[] = response.data.data ?? response.data;
      yield put(projectsActions.fetchProjectsSuccess(projects));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message ?? t("toast_load_failed");
    yield put(projectsActions.fetchProjectsError(message));
    // A locked account (402) is taken care of by the api interceptor (it opens Billing or the locked page);
    // a toast with the server's "Your trial has expired" on top of it contradicted the page (walkthrough M4).
    if (error?.response?.status !== 402) yield put(generalActions.errorAC({ message, type: "error" }));
  }
}

function* createProject(action: any): SagaIterator {
  try {
    const response: { status: number; data: any } = yield call(
      projectsAPI.createProject,
      action.payload
    );
    if (response.status === 200 || response.status === 201) {
      const project: Project = response.data.data ?? response.data;
      yield put(projectsActions.createProjectSuccess(project));
      yield put(generalActions.successAC({ message: t("toast_created"), type: "success" }));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message ?? t("toast_create_failed");
    yield put(generalActions.errorAC({ message, type: "error" }));
  }
}

function* updateProject(action: any): SagaIterator {
  try {
    const response: { status: number; data: any } = yield call(
      projectsAPI.updateProject,
      action.id,
      action.payload
    );
    if (response.status === 200) {
      const project: Project = response.data.data ?? response.data;
      yield put(projectsActions.updateProjectSuccess(project));
      yield put(generalActions.successAC({ message: t("toast_updated"), type: "success" }));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message ?? t("toast_update_failed");
    yield put(generalActions.errorAC({ message, type: "error" }));
  }
}

function* closeProject(action: any): SagaIterator {
  try {
    const response: { status: number } = yield call(projectsAPI.closeProject, action.payload);
    if (response.status === 200) {
      yield put(projectsActions.closeProjectSuccess(action.payload));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message ?? t("toast_close_failed");
    yield put(generalActions.errorAC({ message, type: "error" }));
  }
}

function* openProject(action: any): SagaIterator {
  try {
    const response: { status: number } = yield call(projectsAPI.openProject, action.payload);
    if (response.status === 200) {
      yield put(projectsActions.openProjectSuccess(action.payload));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message ?? t("toast_open_failed");
    yield put(generalActions.errorAC({ message, type: "error" }));
  }
}

function* deleteProject(action: any): SagaIterator {
  try {
    const response: { status: number; data: any } = yield call(
      projectsAPI.deleteProject,
      action.payload
    );
    if (response.status === 204 || response.status === 200) {
      yield put(projectsActions.deleteProjectSuccess(action.payload));
      yield put(generalActions.successAC({ message: t("toast_deleted"), type: "success" }));
    }
  } catch (error: any) {
    const errorData = error?.response?.data;
    // If 422 with confirmation_required the component will handle the confirm dialog
    // We re-throw so the component can catch it via a callback pattern.
    // For now just surface the error if it's not the expected confirmation request.
    if (errorData?.error !== 'confirmation_required') {
      const message = errorData?.message ?? t("toast_delete_failed");
      yield put(generalActions.errorAC({ message, type: "error" }));
    }
    // If confirmation_required — the UI dispatches CONFIRM_DELETE_PROJECT instead
  }
}

function* confirmDeleteProject(action: any): SagaIterator {
  try {
    const response: { status: number; data: any } = yield call(
      projectsAPI.deleteProject,
      action.id,
      action.confirmName
    );
    if (response.status === 204 || response.status === 200) {
      yield put(projectsActions.deleteProjectSuccess(action.id));
      yield put(generalActions.successAC({ message: t("toast_permanently_deleted"), type: "success" }));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message ?? t("toast_delete_failed");
    yield put(generalActions.errorAC({ message, type: "error" }));
  }
}

function* setCurrentProject(action: any): SagaIterator {
  try {
    const response: { status: number } = yield call(
      projectsAPI.setCurrentProject,
      action.payload
    );
    if (response.status === 200) {
      yield put(projectsActions.setCurrentProjectSuccess(action.payload));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message ?? t("toast_set_current_failed");
    yield put(generalActions.errorAC({ message, type: "error" }));
  }
}

function* setProjectFilter(action: any): SagaIterator {
  try {
    const response: { status: number } = yield call(
      projectsAPI.setProjectFilter,
      action.payload
    );
    if (response.status === 200) {
      yield put(projectsActions.setProjectFilterSuccess(action.payload));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message ?? t("toast_filter_save_failed");
    yield put(generalActions.errorAC({ message, type: "error" }));
  }
}

function* bulkMoveRoutes(action: any): SagaIterator {
  try {
    const response: { status: number; data: any } = yield call(
      projectsAPI.bulkMoveRoutes,
      action.routeIds,
      action.targetProjectId
    );
    if (response.status === 200) {
      const { moved, blocked } = response.data;
      if (blocked && blocked.length > 0) {
        const blockedNames = blocked.map((b: any) => b.route_name).join(', ');
        if (moved > 0) {
          yield put(generalActions.successAC({
            message: t("toast_moved_partial", { moved, blocked: blocked.length, names: blockedNames }),
            type: "success",
          }));
        } else {
          yield put(generalActions.errorAC({
            message: t("toast_moved_all_blocked", { names: blockedNames }),
            type: "error",
          }));
        }
      } else {
        yield put(generalActions.successAC({ message: t("toast_moved", { count: moved }), type: "success" }));
      }
      yield put(projectsActions.fetchProjects());
    }
  } catch (error: any) {
    const message = error?.response?.data?.message ?? t("toast_move_failed");
    yield put(generalActions.errorAC({ message, type: "error" }));
  }
}

export function* projectsWatcher() {
  yield takeLatest(actionTypes.FETCH_PROJECTS, fetchProjects);
  yield takeLatest(actionTypes.CREATE_PROJECT, createProject);
  yield takeLatest(actionTypes.UPDATE_PROJECT, updateProject);
  yield takeLatest(actionTypes.CLOSE_PROJECT, closeProject);
  yield takeLatest(actionTypes.OPEN_PROJECT, openProject);
  yield takeLatest(actionTypes.DELETE_PROJECT, deleteProject);
  yield takeLatest(actionTypes.CONFIRM_DELETE_PROJECT, confirmDeleteProject);
  yield takeLatest(actionTypes.SET_CURRENT_PROJECT, setCurrentProject);
  yield takeLatest(actionTypes.SET_PROJECT_FILTER, setProjectFilter);
  yield takeLatest(actionTypes.BULK_MOVE_ROUTES, bulkMoveRoutes);
}
